import { storeToRefs } from 'pinia'
import { watch } from 'vue'

import { useAuthToken } from '@/composables/auth/useAuthToken'
import { useMainStore } from '@/stores/main'
import { toLocaleCodes } from '@/utils/i18nLocales'
import { buildIntentPath, buildLobbyPath, intentOfModalName, isIntentPath, isSyncablePath } from '@/utils/urlModal'

// 登入／註冊彈窗 → 網址的同步層（TI-15592 §1 衍生）。
// 反向的「網址 → 彈窗」由 middleware/url-modal.global.js + plugins/url-modal-normalize.client.js
// + composables/ui/useUrlModalTrigger.js 負責，本層只補上「開窗改網址、關窗還原、上一頁關窗」。
//
// 只碰 router 與 auth 面板本身，不碰 urlModalPending / deferredAnnouncement：公告延後與補跳仍由
// layouts/default.vue 的 watch(getModal) 負責，兩層各管各的旗標才不會互相蓋掉。

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()
  const mainStore = useMainStore()
  const { getModal } = storeToRefs(mainStore)
  const { isLoggedIn } = useAuthToken()

  // owned：目前網址上的 /login、/register 是不是「本層（或深連結）放上去、關窗時該收回」的。
  // 刻意用區域變數而非 store 旗標——store 旗標會被 closeAllModals() 這類連鎖流程
  // 一併清掉（同 TI-15583 spec §7.3 陷阱 2）。放 plugin 閉包內即為一個 app 實例一份。
  let owned = false

  // $i18n 在 plugin 建立當下未必就緒，取值延到 watcher 觸發時
  const localeCodes = () => toLocaleCodes(nuxtApp.$i18n?.locales)

  // query 與 hash 一律原樣帶著走：/login 這段是唯一被增刪的東西
  // 目標與現況相同時不導頁，避免與 error handler 的 closeAllModals() + navigateTo 撞成重複導航
  const go = (method, path) => {
    const current = router.currentRoute.value
    const target = { path, query: { ...current.query }, hash: current.hash }
    if (router.resolve(target).fullPath === current.fullPath) return
    // 網址同步屬背景動作，導航被守衛中止（guest middleware 導 /main）不應變成 unhandled rejection，
    // 真正的錯誤彈窗由 middleware / request 層負責
    router[method](target).catch(() => {})
  }

  // 從子路由（/login）退回沒有子頁的父路由（/）時，nuxtApp._route 不會被同步，
  // useRoute() 會停在 /login —— layout 的 SEO useHead 因此關窗後還掛著登入頁的 title。
  // Nuxt 只在兩種時機同步：router.afterEach 中「最後一個 matched 的元件相同」
  // （nuxt/dist/pages/runtime/plugins/router.js:79-83，這裡不成立），或 <NuxtPage> 的
  // Suspense onResolve（同套件 page.js:135，退回父路由沒有新頁面元件要 resolve，永遠不會來）。
  // 只補「to 是 from 的前綴」這種退回情境：此時不會有 page:finish，補同步不會搶在頁面切換之前。
  const isRetreatToParent = (to, from) => to.matched.length < from.matched.length && to.matched.every((record, i) => record === from.matched[i])

  router.afterEach((to, from) => {
    const locales = localeCodes()

    // 上一頁關窗：開窗時 push 進歷史的那一筆，Back 回來就該把窗一起收掉。
    // 沒有這段的話網址已退回大廳、彈窗卻還蓋在畫面上（getModal 沒變，下方 watch 不會觸發，
    // 子頁 useUrlModalTrigger 也只有 onMounted）。
    //
    // 守衛刻意收得很緊，三個條件缺一不可：
    // 1. owned —— 只收自己放上去的那筆，深連結以外的被動彈窗不受影響
    // 2. 目的地不是意圖路徑 —— /login ↔ /register 互跳（本層自己的 replace）不能被當成離開
    // 3. 目前開著的是 auth 面板 —— 不可放寬成「有 modal 就關」：關窗的 replace 是非同步的，
    //    等 afterEach 跑到時 getModal 可能已經是別人開的下一個彈窗（連續開窗即為此形）。
    //    連帶地，切到 ModalPasswordForget 後按上一頁不會關窗，屬預期行為
    if (owned && !isIntentPath(to.path, locales) && intentOfModalName(getModal.value)) {
      owned = false
      mainStore.setModal(null)
    }

    if (!isSyncablePath(to.path, locales)) return
    if (!isRetreatToParent(to, from)) return
    nuxtApp._route?.sync?.()
  })

  watch(getModal, (modalName) => {
    const { path } = router.currentRoute.value
    const locales = localeCodes()

    // 非大廳頁面（/main*、遊戲頁、未知路徑）不同步網址
    if (!isSyncablePath(path, locales)) return

    // 已登入時登入成功的關窗會與 auth 流程導向 /main 撞在一起，網址交給 auth 流程，
    // 本層只把 owned 收乾淨，避免下次開窗誤判成「已經在目標路徑」而不 push
    if (isLoggedIn.value) {
      owned = false
      return
    }

    const intent = intentOfModalName(modalName)

    if (intent) {
      // 深連結（網址本來就是 /login）：go() 會因目標等於現況而不導頁，但 owned 仍要成立，
      // 關窗才收得回大廳
      owned = true
      // 已經站在 /login、/register 上（含登入↔註冊互跳）用 replace，
      // 免得在歷史裡堆出一串互跳、上一頁退不出去
      const method = isIntentPath(path, locales) ? 'replace' : 'push'
      go(method, buildIntentPath({ path, intent, locales }))
      return
    }

    // 登入面板切到 ModalPasswordForget 這類非 auth 彈窗：網址維持 /login，owned 不變
    if (modalName) return

    // 關窗：只還原自己放上去的那段
    if (!owned) return
    owned = false
    go('replace', buildLobbyPath({ path, locales }))
  })
})
